import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useMatch, useLocation, useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import classnames from 'classnames';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

import { reduxActions, useDispatch, useSelector } from 'store';
import { HeaderTopBar, BottomMenu } from 'components/layouts/MainLayout/components';
import { Loader } from 'components/controls';
import TraderDashboardMenu from 'components/containers/TraderDashboardMenu';
import TraderClientDashboardMenu from 'components/containers/TraderClientDashboardMenu';

import styles from 'theme/components/layouts/TraderPanelLayout/TraderPanelLayout.module.scss';

const TraderPanelLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isCondensed, setIsCondensed] = useState(false);
  const clientRouteMatch = useMatch({ path: '/:locale/trader/clients/:id', end: false });
  const clientIdFromPath = clientRouteMatch?.params?.id;
  const isClientContext = Boolean(clientRouteMatch && clientIdFromPath && clientIdFromPath !== 'new');
  const currentClient = useSelector((state) => state.traderDashboard.currentClient);

  useEffect(() => {
    if (!isClientContext) {
      dispatch(reduxActions.setCurrentClient({ clientId: null, client: null }));
    }
  }, [dispatch, isClientContext]);

  // Po odświeżeniu w kontekście klienta przekieruj na bazowy URL karty klienta (tam pobierane są dane)
  useEffect(() => {
    if (isClientContext && clientIdFromPath && clientIdFromPath !== 'new' && !currentClient) {
      navigate(`/${i18n.language}/trader/clients/${clientIdFromPath}`, { replace: true });
    }
  }, [isClientContext, clientIdFromPath, currentClient, navigate, i18n.language]);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname]);

  return (
    <>
      <Helmet>
        <link
          rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.13.1/font/bootstrap-icons.min.css"
        />
      </Helmet>
      <div
        className={classnames(
          styles.componentWrapper,
          'StylePath-Components-Layouts-TraderPanelLayout'
        )}>
        <main>
          <header>
            <HeaderTopBar simpleHeader={false} isTraderPanel />
          </header>
          <div ref={contentRef} className={styles.content}>
            <Grid container columnSpacing="32px">
              <Grid
                item
                md={isCondensed ? 1 : 2.5}
                xs={12}
                className={classnames(styles.menuWrapper, { [styles.menuWrapperCondensed]: isCondensed })}>
                {isClientContext ? (
                  <TraderClientDashboardMenu
                    isCondensed={isCondensed}
                    onToggleCondensed={() => setIsCondensed((v) => !v)}
                  />
                ) : (
                  <TraderDashboardMenu
                    isCondensed={isCondensed}
                    onToggleCondensed={() => setIsCondensed((v) => !v)}
                  />
                )}
              </Grid>
              <Grid item md={isCondensed ? 11 : 9.5} xs={12}>
                <Suspense fallback={<Loader />}>
                  <Outlet />
                </Suspense>
              </Grid>
            </Grid>
          </div>
          <BottomMenu />
        </main>
      </div>
    </>
  );
};

export default TraderPanelLayout;
